/* eslint-disable react/no-unknown-property */
/** @jsxImportSource @emotion/react */
import Box from '@mui/material/Box';
import { useTheme } from '@emotion/react';
import PhoneNumberTextFieldCustom from '@mvf/external-components/Components/Molecules/PhoneNumberTextFieldCustom';
import { useCallback, useEffect, useRef, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { TRANSITION_TIMEOUT_TIME } from '../../Data/Constants';
import ValidationStatus from '../../Data/ValidationStatus';
import SagaStatus from '../../Data/SagaStatus';
import { getFirstEditableComponentOnPage } from '../../Helpers/components';
import {
  clearConstraintsValidations,
  setBrowserAutoFilledActive,
  setConstraintMessage,
  updateValue,
  setPhoneValidationConstraint,
  setComponentValidationStatus,
} from '../../Lib/redux/slices/components';

import { setPhoneNumberCountryCode } from '../../Lib/redux/slices/formConfig';
import {
  setLegalStatement,
  updateIsLegalStatementRevealed,
  updateShouldRenderLegalStatement,
} from '../../Lib/redux/slices/legalStatement';
import { setShouldNavigateForward } from '../../Lib/redux/slices/navigationControls';
import { setComponentIdToReceiveFocus } from '../../Lib/redux/slices/persistKeyboard';
import QuestionText from '../QuestionText/QuestionText';
import getPhoneNumberErrorMessage from './phoneNumberInput.helpers';
import makePhoneNumberInputStyling from './usePhoneNumberInputStyles';
import Subcategories from '../../Data/Subcategories';
import { isLastPageBeforeSubmission } from '../../Hooks/useNavigation/useNavigation.helpers';
import { setIsFinalPageBeforeSubmission } from '../../Lib/redux/slices/navigation';
import { SELECT_COUNTRY_MAPPED_QUESTION_ID } from '../../Data/Country';
import { useAutoFocusFirstEditableComponent } from '../../Hooks/useAutoFocusFirstEditableComponent';
import { renderMustache } from '../../Helpers/mustache';
import { ComponentTypesToReceiveFocusOnPageEnter } from '../../Data/Components';
import validatePhone from './validatePhone.helper';

const PhoneNumberInput = ({
  componentId,
  displayQuestionTextInsideTextfield,
  errorMessage,
  iFrameId,
  componentStatus,
  placeholder,
  subheading,
  text,
  value,
  isAutoFilledActive,
  formId,
}) => {
  const dispatch = useDispatch();
  const styling = makePhoneNumberInputStyling(useTheme());
  const component = useSelector(
    (i) => i.components[`component_${componentId}`]
  );
  const isAnswered =
    componentStatus === ValidationStatus.VALID ||
    componentStatus === ValidationStatus.INVALID;
  const [isAutoFilled, setIsAutoFilled] = useState(false);

  const {
    countryCode,
    phoneNumberCountryCode,
    isSafari,
    isIos,
    isAndroid,
    subcategoryId,
    submissionPageNumber,
  } = useSelector((i) => i.formConfig);
  const { dynamicNewsletterName } = useSelector((store) => store.newsletter);
  const components = useSelector((store) => store.components);
  const { pages } = useSelector((store) => store.paging);
  const { currentPage } = useSelector((store) => store.navigation);
  const { componentIdToReceiveFocus } = useSelector(
    (store) => store.persistKeyboard
  );
  const { componentIdToSagaStatus } = useSelector(
    (store) => store.dataMappings
  );
  const widgetResizeDuration = useSelector(
    (store) => store.optionalFormConfig.widgetResizeDuration
  );
  const [attemptNavigate, setAttemptNavigate] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(
    phoneNumberCountryCode || countryCode || 'US'
  );

  const [latestValidationResult, setLatestValidationResult] = useState(null);
  const [countryChangeTrigger, setCountryChangeTrigger] = useState(null);

  const handleBlur = () => {
    if (value === '' || value === null || value === undefined) {
      return;
    }
    if (isAutoFilledActive) {
      dispatch(setBrowserAutoFilledActive({ componentId, value: false }));
    }

    const currentCountryCode =
      phoneNumberCountryCode || selectedCountry || countryCode;
    dispatch(clearConstraintsValidations({ componentId }));

    if (
      latestValidationResult !== null &&
      latestValidationResult?.isValid === false
    ) {
      dispatch(
        setPhoneValidationConstraint({
          status: ValidationStatus.INVALID,
          componentId,
        })
      );
      dispatch(
        setComponentValidationStatus({
          componentId,
          countryCode,
          iFrameId,
          formId,
        })
      );
    }
    // we attempt to use the result of the external phone component's validation if available, otherwise just rely on redux state
    const shouldValidate =
      (value &&
        value.trim() !== '' &&
        latestValidationResult !== null &&
        latestValidationResult?.isValid === true) ||
      (latestValidationResult === null && value && value.trim() !== '');

    if (shouldValidate) {
      validatePhone({
        value,
        componentId,
        countryCode: currentCountryCode,
        dispatch,
        iFrameId,
        constraints: component.validation?.constraints,
        formId,
      });
    }
  };

  // Handle phone number changes (user input)
  const handlePhoneNumberChange = ({ phoneNumber }) => {
    if (phoneNumber === undefined || phoneNumber === null) {
      return;
    }
    dispatch(
      updateValue({
        componentId,
        value: phoneNumber,
      })
    );
  };

  const updatePhoneNumberErrorMessage = useCallback(
    (currentPhoneNumberCountryCode) => {
      dispatch(
        setConstraintMessage({
          componentId,
          constraint: 'phone',
          message: getPhoneNumberErrorMessage(currentPhoneNumberCountryCode),
        })
      );
    },
    [dispatch, componentId]
  );

  // set's the error message for the phone number input on load
  useEffect(() => {
    if (selectedCountry) {
      updatePhoneNumberErrorMessage(selectedCountry.toUpperCase());
    }
  }, [selectedCountry, updatePhoneNumberErrorMessage]);

  useEffect(() => {
    if (phoneNumberCountryCode && phoneNumberCountryCode !== selectedCountry) {
      setSelectedCountry(phoneNumberCountryCode);
    }
  }, [phoneNumberCountryCode, selectedCountry]);

  // Handle validation after country change
  useEffect(() => {
    if (countryChangeTrigger) {
      const {
        newCountryCode,
        previousCountryCode,
        currentPhoneNumberCountryCode,
      } = countryChangeTrigger;
      if (latestValidationResult) {
        const { formattedNumber } = latestValidationResult;
        if (
          formattedNumber &&
          formattedNumber.trim() !== '' &&
          formattedNumber.length > 6
        ) {
          const newValue = formattedNumber.replace(
            previousCountryCode,
            newCountryCode
          );

          dispatch(
            updateValue({
              componentId,
              value: newValue,
            })
          );

          // Reset validation result since country changed - previous validation is no longer relevant
          setLatestValidationResult(null);

          validatePhone({
            value: newValue,
            componentId,
            countryCode: currentPhoneNumberCountryCode,
            dispatch,
            iFrameId,
            constraints: component.validation?.constraints,
            formId,
          });
        }
        setCountryChangeTrigger(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    countryChangeTrigger,
    componentId,
    dispatch,
    iFrameId,
    formId,
    component.validation?.constraints,
  ]);

  const handleCountryCodeChange = ({
    selectedCountry: selectedCountryOption,
    previousCountry: previousCountryOption,
  }) => {
    const newCountryISOCode = selectedCountryOption.code;
    const newCountryCode = selectedCountryOption.dialCode;
    const previousCountryCode = previousCountryOption.dialCode;
    setSelectedCountry(newCountryISOCode);
    const currentPhoneNumberCountryCode = newCountryISOCode.toUpperCase();
    if (currentPhoneNumberCountryCode !== '') {
      updatePhoneNumberErrorMessage(currentPhoneNumberCountryCode);
      batch(() => {
        dispatch(
          setPhoneNumberCountryCode({
            phoneNumberCountryCode: currentPhoneNumberCountryCode,
          })
        );

        const selectedCountryIsUSA = Object.values(components).find(
          (componentFromList) => {
            return (
              componentFromList.component.mappedQuestionId ===
                SELECT_COUNTRY_MAPPED_QUESTION_ID &&
              componentFromList.input?.value?.trim() === 'USA'
            );
          }
        );

        if (
          countryCode !== 'US' &&
          !(
            subcategoryId === Subcategories.REMOVALS_AUTOQUALIFIED.id &&
            selectedCountryIsUSA
          )
        ) {
          // We don't re-request a statement for US forms, but for others, here we invalidate the legalStatement so a new one is requested in LegalStatement
          dispatch(setLegalStatement({ statement: null }));
        }

        if (subcategoryId === Subcategories.REMOVALS_AUTOQUALIFIED.id) {
          const isFinalPageBeforeSubmissionValue = isLastPageBeforeSubmission({
            page: currentPage,
            components,
            pages,
            submissionPageNumber,
            dynamicNewsletterName,
            phoneNumberCountryCode: currentPhoneNumberCountryCode,
            subcategoryId,
          });

          // This is sufficient to get the legal statement visibility to change with Dynamic height turned off
          dispatch(
            setIsFinalPageBeforeSubmission({
              isFinalPageBeforeSubmission: isFinalPageBeforeSubmissionValue,
            })
          );

          // If dynamic height is turned on, we need to update this field too otherwise the legal statement will not budge
          const shouldRenderLegalStatementValue =
            isFinalPageBeforeSubmissionValue;
          dispatch(
            updateShouldRenderLegalStatement({
              shouldRenderLegalStatement: shouldRenderLegalStatementValue,
            })
          );
          dispatch(
            updateIsLegalStatementRevealed({
              isLegalStatementRevealed: shouldRenderLegalStatementValue,
            })
          );
        }
      });

      // Set trigger for useEffect to handle validation
      setCountryChangeTrigger({
        newCountryCode,
        previousCountryCode,
        currentPhoneNumberCountryCode,
      });
    }
  };

  const handleClick = () => {
    dispatch(clearConstraintsValidations({ componentId }));
  };

  const handleValidationChange = (params) => {
    if (componentStatus !== ValidationStatus.VALID) {
      dispatch(clearConstraintsValidations({ componentId }));
    }
    setLatestValidationResult(params.validationResult);
  };

  const handleEnterPress = (event) => {
    if (event.key === 'Enter') {
      const firstEditableComponentOnNextPage = getFirstEditableComponentOnPage(
        component.component.page + 1,
        components,
        pages
      );

      if (
        (isIos || isAndroid) &&
        ComponentTypesToReceiveFocusOnPageEnter.includes(
          firstEditableComponentOnNextPage?.input?.type
        )
      ) {
        dispatch(
          setComponentIdToReceiveFocus({
            componentIdToReceiveFocus:
              firstEditableComponentOnNextPage.component.id,
          })
        );
      }

      if (value && value !== '' && value !== null) {
        const currentCountryCode =
          phoneNumberCountryCode || selectedCountry || countryCode;
        dispatch(clearConstraintsValidations({ componentId }));
        if (
          value &&
          value.trim() !== '' &&
          latestValidationResult?.isValid === true
        ) {
          validatePhone({
            value,
            componentId,
            countryCode: currentCountryCode,
            dispatch,
            iFrameId,
            constraints: component.validation?.constraints,
            formId,
          });
          setAttemptNavigate(true);
        } else if (
          value &&
          value.trim() !== '' &&
          latestValidationResult?.isValid === false
        ) {
          dispatch(
            setPhoneValidationConstraint({
              status: ValidationStatus.INVALID,
              componentId,
            })
          );
          dispatch(
            setComponentValidationStatus({
              componentId,
              countryCode,
              iFrameId,
              formId,
            })
          );
        }
      }
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleEnterPress(event);
      return;
    }

    // Browser auto fill currently triggers key down events with the following key and code
    if (event.key === 'Unidentified' && event.code === undefined) {
      setIsAutoFilled(true);
      dispatch(setBrowserAutoFilledActive({ componentId, value: true }));

      setTimeout(() => {
        const autofillValue = inputRef.current?.value;
        if (
          autofillValue &&
          autofillValue.trim() !== '' &&
          autofillValue !== value
        ) {
          dispatch(updateValue({ componentId, value: autofillValue }));
        }
      }, 25);
    } else {
      setIsAutoFilled(false);
      dispatch(setBrowserAutoFilledActive({ componentId, value: false }));
    }
  };

  useEffect(() => {
    if (isAutoFilled) {
      const autofillValue = inputRef.current?.value;
      if (
        autofillValue &&
        autofillValue.trim() !== '' &&
        autofillValue !== value
      ) {
        dispatch(updateValue({ componentId, value: autofillValue }));
      }
      setIsAutoFilled(false);
    }
  }, [componentId, dispatch, isAutoFilled, value]);

  // sagaStatus is only needed if the phone plugin did not load and we fallback to validation via the gateway
  const sagaStatus = componentIdToSagaStatus?.[componentId];

  useEffect(() => {
    // Only proceed if we're not in a pending state for this component
    const isValidationComplete =
      !sagaStatus || sagaStatus === SagaStatus.COMPLETE;

    if (
      componentStatus === ValidationStatus.VALID &&
      attemptNavigate &&
      isValidationComplete
    ) {
      setAttemptNavigate(false);
      dispatch(setShouldNavigateForward({ shouldNavigateForward: true }));
    }
    if (
      componentStatus === ValidationStatus.INVALID &&
      attemptNavigate &&
      isValidationComplete
    ) {
      setAttemptNavigate(false);
    }
  }, [attemptNavigate, componentStatus, componentId, sagaStatus, dispatch]);

  const inputRef = useRef(null);
  const hiddenInputRef = useRef(null);
  useAutoFocusFirstEditableComponent({ componentId, inputRef });

  useEffect(() => {
    if (inputRef.current && componentIdToReceiveFocus === componentId) {
      hiddenInputRef.current.focus({ preventScroll: true });
      // We need to wait for the transition to finish before focusing on the input
      // The enterPhase/exitPhase state values overlap on component mount in the middle of the transition (i.e. both are false)
      // So we need to wait for the transition time + 100ms to ensure the transition has finished, otherwise the phone plugin causes glitchy UI behaviour
      setTimeout(() => {
        inputRef.current.focus({ preventScroll: true });
      }, widgetResizeDuration || TRANSITION_TIMEOUT_TIME + 100);
      dispatch(
        setComponentIdToReceiveFocus({ componentIdToReceiveFocus: null })
      );
    }
  }, [componentIdToReceiveFocus, componentId, dispatch, widgetResizeDuration]);

  return (
    <Box data-testid="phoneBox" onKeyDown={handleEnterPress} css={styling.box}>
      {!displayQuestionTextInsideTextfield && (
        <QuestionText
          heading={renderMustache({
            template: text,
            components,
            currentComponent: component,
            iFrameId,
            formId,
          })}
          subheading={renderMustache({
            template: subheading,
            components,
            currentComponent: component,
            iFrameId,
            formId,
          })}
        />
      )}
      <input
        type="text"
        ref={hiddenInputRef}
        id="hidden-input-for-persistent-keyboard"
        css={styling.hiddenInputForPersistentKeyboard}
      />
      <Box css={styling.textFieldBox}>
        <PhoneNumberTextFieldCustom
          autoComplete="section-parent tel"
          errorMessage={errorMessage}
          id={`phoneNumberTextField-${componentId}`}
          isAnswered={isAnswered}
          isAutoFilledActive={isAutoFilledActive}
          isSafari={isSafari}
          label={!displayQuestionTextInsideTextfield ? '' : text}
          value={value}
          initialCountry={selectedCountry}
          onCountryChange={handleCountryCodeChange}
          onPhoneNumberChange={handlePhoneNumberChange}
          onValidationChange={handleValidationChange}
          onClick={handleClick}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          type="tel"
          placeholder={placeholder}
          inputRef={inputRef}
          useModalForCountrySelection
          inputProps={{
            'data-testid': 'phoneNumberTextField',
          }}
        />
      </Box>
    </Box>
  );
};

export default PhoneNumberInput;
